import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiBell,
  FiChevronRight,
  FiCommand,
  FiSettings,
  FiUser,
  FiLogOut,
  FiHelpCircle,
  FiShield,
  FiUsers
} from 'react-icons/fi';
import { performLogoutAndRedirect } from '../../utils/logout';

// Page title mapping
const PAGE_TITLES = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Overview of your operations' },
  '/dispatch': { title: 'Dispatch', subtitle: 'Manage order dispatch operations' },
  '/tickets': { title: 'Ticket Queue', subtitle: 'Manage incoming dispatch tasks' },
  '/tickets/admin': { title: 'Command Center', subtitle: 'Monitor team performance and tickets' },
  '/orders/live': { title: 'Live Orders', subtitle: 'Real-time order tracking' },
  '/orderList': { title: 'Orders', subtitle: 'View and manage all orders' },
  '/hostList': { title: 'Property Hosts', subtitle: 'Manage host accounts and properties' },
  '/driverList': { title: 'Concierge Team', subtitle: 'Manage concierge accounts' },
  '/announcements': { title: 'Announcements', subtitle: 'Manage system announcements' },
  '/inquiries': { title: 'Inquiries', subtitle: 'Customer inquiries and support' },
  '/manage-order-status': { title: 'Order Status', subtitle: 'Update order statuses' },
  '/assign-property-store': { title: 'Assign Store', subtitle: 'Assign stores to properties' },
  '/assign-additional-delivery': { title: 'Delivery Fee', subtitle: 'Manage delivery fees' },
  '/settings': { title: 'Settings', subtitle: 'Application settings' },
};

const Header = ({ 
  title: customTitle, 
  subtitle: customSubtitle,
  breadcrumbs = [],
  actions = null,
  onSearch,
  showSearch = true,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { adminInfo } = useSelector((state) => state.login);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Get page info from path or use custom
  const pageInfo = PAGE_TITLES[location.pathname] || { title: 'Page', subtitle: '' };
  const title = customTitle || pageInfo.title;
  const subtitle = customSubtitle || pageInfo.subtitle;

  // Mock notifications
  const notifications = [
    { id: 1, title: 'New P0 Ticket', message: 'Concierge support needed', time: '2 min ago', unread: true },
    { id: 2, title: 'Order Completed', message: 'Order #78234 delivered', time: '15 min ago', unread: true },
    { id: 3, title: 'Concierge Assigned', message: 'Mike accepted order #78235', time: '1 hour ago', unread: false },
  ];

  const unreadCount = notifications.filter(n => n.unread).length;
  const isAdmin = adminInfo?.isAdmin || adminInfo?.role === 'admin';
  const role = adminInfo?.role || 'support'; // Use role for UI differentiation

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch && searchQuery.trim()) {
      onSearch(searchQuery);
    }
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    performLogoutAndRedirect(dispatch);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-zinc-200">
      <div className="h-16 px-6 flex items-center justify-between gap-4">
        {/* Left: Title & Breadcrumbs */}
        <div className="flex-1 min-w-0">
          {breadcrumbs.length > 0 ? (
            <nav className="flex items-center gap-1.5 text-sm mb-0.5">
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={crumb.path || idx}>
                  {idx > 0 && <FiChevronRight className="w-3.5 h-3.5 text-zinc-400" />}
                  {crumb.path ? (
                    <button
                      onClick={() => navigate(crumb.path)}
                      className="text-zinc-500 hover:text-zinc-700 transition-colors"
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    <span className="text-zinc-900 font-medium">{crumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          ) : null}
          
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-zinc-900 tracking-tight">{title}</h1>
            {subtitle && (
              <span className="text-sm text-zinc-500 hidden sm:block">
                {subtitle}
              </span>
            )}
          </div>
        </div>

        {/* Center: Search */}
        {showSearch && (
          <form onSubmit={handleSearch} className="hidden md:block">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-64 lg:w-80 pl-10 pr-12 py-2 bg-zinc-100 border-0 rounded-xl text-sm placeholder:text-zinc-400 focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-0.5 text-zinc-400">
                <FiCommand className="w-3 h-3" />
                <span className="text-xs font-medium">K</span>
              </div>
            </div>
          </form>
        )}

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Role from backend only — no client override */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl border border-zinc-200 bg-white shadow-sm">
            <span className="text-[11px] font-semibold text-zinc-600">Role:</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold ${
              role === 'admin' ? 'bg-indigo-600 text-white' : 'bg-zinc-900 text-white'
            }`}>
              {role === 'admin' ? <FiShield className="w-3.5 h-3.5" /> : <FiUsers className="w-3.5 h-3.5" />}
              {role === 'admin' ? 'Admin' : 'Support'}
            </span>
          </div>

          {/* Custom Actions */}
          {actions}

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2.5 rounded-xl text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <FiBell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowNotifications(false)} 
                />
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl border border-zinc-200 overflow-hidden z-50 animate-scale-in">
                  <div className="p-4 border-b border-zinc-100">
                    <h3 className="font-semibold text-zinc-900">Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`p-4 border-b border-zinc-50 hover:bg-zinc-50 cursor-pointer transition-colors ${
                          notif.unread ? 'bg-indigo-50/50' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {notif.unread && (
                            <div className="mt-1.5 w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
                          )}
                          <div className={notif.unread ? '' : 'ml-5'}>
                            <p className="text-sm font-medium text-zinc-900">{notif.title}</p>
                            <p className="text-sm text-zinc-500">{notif.message}</p>
                            <p className="text-xs text-zinc-400 mt-1">{notif.time}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 border-t border-zinc-100">
                    <button className="w-full py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors">
                      View all notifications
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-zinc-100 transition-colors"
            >
              <div className="h-9 rounded-full px-3 inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-rose-500 text-white shadow-sm border border-white/20">
                <span className="text-sm font-semibold leading-none">Bellhopt</span>
                <span className="w-2 h-2 rounded-full bg-white/80" aria-hidden />
              </div>
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowUserMenu(false)} 
                />
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-zinc-200 overflow-hidden z-50 animate-scale-in">
                  <div className="p-4 border-b border-zinc-100">
                    <p className="font-semibold text-zinc-900">
                      {adminInfo?.firstName} {adminInfo?.lastName}
                    </p>
                    <p className="text-sm text-zinc-500">{adminInfo?.email}</p>
                    <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-zinc-100 text-zinc-700 text-xs font-semibold">
                      Role: {isAdmin ? 'Admin' : 'Support'}
                    </div>
                  </div>
                  <div className="p-2">
                    <button
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-zinc-700 hover:bg-zinc-100 transition-colors"
                      onClick={() => { navigate('/profile'); setShowUserMenu(false); }}
                    >
                      <FiUser className="w-4 h-4" />
                      Profile
                    </button>
                    <button
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-zinc-700 hover:bg-zinc-100 transition-colors"
                      onClick={() => { navigate('/settings'); setShowUserMenu(false); }}
                    >
                      <FiSettings className="w-4 h-4" />
                      Settings
                    </button>
                    <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-zinc-700 hover:bg-zinc-100 transition-colors">
                      <FiHelpCircle className="w-4 h-4" />
                      Help & Support
                    </button>
                    <div className="mt-2 border-t border-zinc-100 pt-2">
                      <p className="text-xs font-semibold text-zinc-500">Role from session</p>
                      <p className="text-sm font-medium text-zinc-800 mt-0.5">{isAdmin ? 'Admin' : 'Support'}</p>
                    </div>
                  </div>
                  <div className="p-2 border-t border-zinc-100">
                    <button
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                      onClick={handleLogout}
                    >
                      <FiLogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

